import React, { useState, useEffect } from "react";
import { Colors } from "../../utils/colors";
import { usePaymentStore } from "../../stores/StoreContext";

declare global {
  interface Window {
    Accept: {
      dispatchData: (
        secureData: any,
        responseHandler: (response: any) => void
      ) => void;
    };
  }
}

type props = {};

export const AddCardForm: React.FC<props> = () => {
  const paymentStore = usePaymentStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Load the Accept.js script
  useEffect(() => {
    const script = document.createElement("script");

    // Use the sandbox URL for development, switch to production for live
    script.src =
      process.env.REACT_APP_AUTHORIZE_NET_URL &&
      process.env.REACT_APP_AUTHORIZE_NET_URL != null &&
      process.env.REACT_APP_AUTHORIZE_NET_URL != ""
        ? process.env.REACT_APP_AUTHORIZE_NET_URL
        : "https://jstest.authorize.net/v1/Accept.js";
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate form inputs
    if (!cardNumber || !expMonth || !expYear || !cardCode || !zipCode) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    // Create the card data object
    const secureData = {
      authData: {
        clientKey: process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY,
        apiLoginID: process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID,
      },
      cardData: {
        cardNumber,
        month: expMonth,
        year: expYear,
        cardCode,
        zip: zipCode,
      },
    };

    // Call Accept.js to get the payment nonce
    if (window.Accept) {
      window.Accept.dispatchData(secureData, (response) => {
        if (response.messages.resultCode === "Error") {
          let errorMessage = "Payment processing error";
          if (
            response.messages.message &&
            response.messages.message.length > 0
          ) {
            errorMessage = `${response.messages.message[0].code}: ${response.messages.message[0].text}`;
          }
          setError(errorMessage);
          setLoading(false);
        } else {
          console.log("Authorize.Net data received:", response);

          try {
            paymentStore.addNewPaymentCard(
              response.opaqueData.dataValue,
              zipCode
            );
            // Reset form
            setCardNumber("");
            setExpMonth("");
            setExpYear("");
            setCardCode("");
            setZipCode("");
          } catch (error) {
            console.error("Error in addNewPaymentCard:", error);
            setError("Error saving card details");
          } finally {
            setLoading(false);
          }
        }
      });
    } else {
      setError("Payment processing is not available. Please try again later.");
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div
          className="form-container"
          style={{ maxWidth: "400px", margin: "0 auto" }}
        >
          <h3
            style={{
              fontSize: "24px",
              color: "#333",
              marginBottom: "14px",
              fontWeight: "500",
              textAlign: "center",
            }}
          >
            Enter Card Details
          </h3>
          <h3
            style={{
              fontSize: "14px",
              color: "#333",
              marginBottom: "20px",
              textAlign: "center",
              fontWeight: "400",
            }}
          >
            I authorize to charge my credit card above for the agreed upon
            purchases. I understand that my information will be saved on file
            for future transactions on my account.
          </h3>

          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="cardNumber"
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "14px",
                color: Colors.grey,
              }}
            >
              Card Number
            </label>
            <input
              id="cardNumber"
              type="text"
              placeholder="XXXX XXXX XXXX XXXX"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, ""))}
              style={{
                width: "100%",
                padding: "12px 15px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                boxSizing: "border-box",
              }}
              maxLength={16}
              required
            />
          </div>

          <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
            <div style={{ flex: 1 }}>
              <label
                htmlFor="expMonth"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontSize: "14px",
                  color: Colors.grey,
                }}
              >
                Month
              </label>
              <input
                id="expMonth"
                type="text"
                placeholder="MM"
                value={expMonth}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (
                    val === "" ||
                    (parseInt(val) > 0 && parseInt(val) <= 12)
                  ) {
                    setExpMonth(val);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                maxLength={2}
                required
              />
            </div>

            <div style={{ flex: 1 }}>
              <label
                htmlFor="expYear"
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontSize: "14px",
                  color: Colors.grey,
                }}
              >
                Year
              </label>
              <input
                id="expYear"
                type="text"
                placeholder="YY"
                value={expYear}
                onChange={(e) => setExpYear(e.target.value.replace(/\D/g, ""))}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                maxLength={2}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="cardCode"
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "14px",
                color: Colors.grey,
              }}
            >
              CVV Code
            </label>
            <input
              id="cardCode"
              type="text"
              placeholder="CVC"
              value={cardCode}
              onChange={(e) => setCardCode(e.target.value.replace(/\D/g, ""))}
              style={{
                width: "100%",
                padding: "12px 15px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                boxSizing: "border-box",
              }}
              maxLength={4}
              required
            />
          </div>

          <div style={{ marginBottom: "25px" }}>
            <label
              htmlFor="zipCode"
              style={{
                display: "block",
                marginBottom: "5px",
                fontSize: "14px",
                color: Colors.grey,
              }}
            >
              Billing Zip Code
            </label>
            <input
              id="zipCode"
              type="text"
              placeholder="Zip Code"
              value={zipCode}
              onChange={(e) =>
                setZipCode(e.target.value.replace(/[^\d-]/g, ""))
              }
              style={{
                width: "100%",
                padding: "12px 15px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "16px",
                boxSizing: "border-box",
              }}
              maxLength={10}
              required
            />
          </div>

          {error && (
            <div
              style={{
                color: "red",
                marginBottom: "15px",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: Colors.darkBrown,
              borderRadius: "100px",
              width: "100%",
              height: "48px",
              border: "none",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              fontSize: "16px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "opacity 0.2s ease",
            }}
          >
            {loading ? "Processing..." : "Add Card"}
          </button>
        </div>
      </form>
    </div>
  );
};
